import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { titleCaseName, titleCaseCompany } from '../lib/nameCase';

const FormContext = createContext(null);
const API_URL = process.env.REACT_APP_BACKEND_URL;

// Local-storage backup so a refresh, accidental tab close, or phone going to
// sleep doesn't wipe the user's progress. The data lives only in the user's
// own browser — nothing is sent to our server. Auto-expires after 24 hours
// and is cleared explicitly on successful submission via resetForm().
const STORAGE_KEY = 'applydrive:form-progress:v1';
const STORAGE_TTL_MS = 24 * 60 * 60 * 1000; // 24h

const readSavedProgress = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.savedAt || !parsed?.formData) return null;
    if (Date.now() - parsed.savedAt > STORAGE_TTL_MS) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

// Quick heuristic — is the form actually populated enough to be worth backing up?
// We don't want to spam localStorage writes on every minor keystroke before the
// user has done anything meaningful, and we don't want to prompt "resume?" if
// they literally just entered a dealer name.
const hasMeaningfulProgress = (data) => {
  if (!data) return false;
  if (data.first_name?.trim() || data.surname?.trim()) return true;
  if (data.email_address?.trim()) return true;
  if (data.mobile_number?.trim()) return true;
  if (data.vehicle_registration?.trim()) return true;
  if (data.addresses?.[0]?.postcode?.trim()) return true;
  return false;
};

const initialFormData = {
  // Personal Details
  title: '',
  first_name: '',
  surname: '',
  date_of_birth: '',
  nationality: 'British',
  marital_status: '',
  
  // Driving & Passport
  driving_licence_type: '',
  has_uk_passport: null,
  
  // Dependants
  dependants: 0,
  
  // Gender
  gender: '',
  
  // Address
  addresses: [{
    postcode: '',
    house_number: '',
    house_name: '',
    street: '',
    town: '',
    years: 0,
    months: 0,
    residential_status: ''
  }],
  
  // Employment
  employment_status: '',
  employer: '',
  job_title: '',
  employment_years: 0,
  employment_months: 0,
  monthly_income: '',
  gross_monthly_income: '',
  monthly_mortgage_rent: '',
  has_additional_income: '',
  additional_incomes: [],
  employer_phone: '',
  employer_address: {
    postcode: '',
    building_number: '',
    street: '',
    town: ''
  },
  employer_address_same_as_home: false,
  employment_history: [{
    employer: '',
    job_title: '',
    employment_status: '',
    years: 0,
    months: 0,
    employer_phone: '',
    employer_address_same_as_home: false,
    employer_address: { postcode: '', building_number: '', street: '', town: '' }
  }],
  gross_annual_income: '',
  
  // Bank Details
  sort_code: '',
  account_number: '',
  bank_name: '',
  bank_years: 0,
  bank_months: 0,
  
  // Vehicle Details
  vehicle_registration: '',
  vehicle_mileage: '',
  vehicle_type: 'Car',
  dealer_name: '',
  vat_qualifying: false, // VAT qualifying vehicle checkbox
  // DVLA lookup fields
  vehicle_make: '',
  vehicle_model: '',
  vehicle_colour: '',
  vehicle_year: '',
  vehicle_fuel_type: '',
  vehicle_engine_capacity: '',
  vehicle_date_first_registered: '',
  vehicle_co2_emissions: '',
  vehicle_tax_status: '',
  vehicle_mot_status: '',
  vehicle_mot_expiry: '',
  vehicle_derivative: '',
  mot_last_mileage: '',
  vehicle_reg_unknown: false,
  vehicle_description: '',
  mot_last_date: '',
  
  // Financials
  cash_price: '',
  borrow_amount: '', // Same as cash_price - for CarMoney form
  deposit: '',
  part_exchange: '',
  settlement: '',
  finance_type: 'HP', // HP or PCP, default HP
  term: 60, // Default 60 months for HP
  annual_mileage: 10000, // PCP only - estimated annual miles
  
  // Application Type - Private (default) or Business
  application_type: 'Private',
  
  // Business Details (only used if application_type is Business)
  business_details: {
    business_type: '',
    industry: '',
    business_name: '',
    main_telephone: '',
    date_of_incorporation: '',
    previous_business_name: '',
    registration_number: '',
    vat_number: '',
    country_of_operation: '',
    country_of_incorporation: '',
    business_description: '',
    director_guarantor: false,
    business_address: {
      premises_type: 'Leased',
      postcode: '',
      building: '',
      street: '',
      town: '',
      county: '',
      years: 0,
      months: 0
    }
  },
  
  // Contact
  mobile_number: '',
  email_address: '',
  
  // Marketing
  receive_marketing: false,
  
  // Additional Notes
  additional_notes: ''
};

export const FormProvider = ({ children }) => {
  const [formData, setFormData] = useState(initialFormData);
  const [currentStep, setCurrentStep] = useState(0);
  const [extractedData, setExtractedData] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [editingFromReview, setEditingFromReview] = useState(false);
  // 'full' = applicant has a specific vehicle in mind
  // 'credit-line' = applicant wants a pre-approval / soft search only, no vehicle
  //   chosen yet. The wizard hides the Vehicle step inputs, the Review step
  //   shows a "Finance check only" notice, and the backend submission omits
  //   vehicle data so Artis assigns a dummy car automatically.
  // null = user hasn't chosen yet. The Vehicle step shows only the choice card,
  //   nothing else, so the choice is unmissable. Cleared on resetForm() and
  //   auto-set to 'full' when the form is populated from OCR extraction (the
  //   dealer has already pasted vehicle data — they know what they're applying for).
  const [applicationMode, setApplicationMode] = useState(null);
  // Holds the saved-progress blob detected on mount, so the UI can offer a
  // "Resume your previous application?" prompt. Cleared once the user decides.
  // Lazy initialiser reads from localStorage during the initial render rather
  // than in a mount effect — both work, but this keeps lint happy and avoids
  // a flash of "no resume" on first paint.
  const [pendingResume, setPendingResume] = useState(() => {
    const saved = readSavedProgress();
    return saved && hasMeaningfulProgress(saved.formData) ? saved : null;
  });
  // Ephemeral mode — set when the form is populated from OCR extraction (the
  // "Staff: Upload existing proposal" path). We deliberately DON'T persist
  // an applicant's data to the staff member's device. The Resume modal would
  // otherwise leak Customer A's data into the next session for Customer B.
  // Manual (Start Application) flow keeps localStorage backup as before.
  const [isEphemeral, setIsEphemeral] = useState(false);

  // Auto-save formData to localStorage whenever it changes (debounced via
  // useEffect's natural batching). Only saves once there's something worth
  // saving — prevents writing empty blobs on initial render. Skipped entirely
  // when the form was populated via OCR extraction (staff-driven flow).
  useEffect(() => {
    if (isEphemeral) return;
    if (!hasMeaningfulProgress(formData)) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        savedAt: Date.now(),
        currentStep,
        formData,
        applicationMode,  // persist so Resume restores wizard mode (full / credit-line)
      }));
    } catch {
      // localStorage full / disabled / private-mode — silent fail, not critical.
    }
  }, [formData, currentStep, isEphemeral, applicationMode]);

  // Warn before tab close / refresh if there's meaningful unsubmitted progress.
  useEffect(() => {
    const handler = (e) => {
      if (hasMeaningfulProgress(formData)) {
        e.preventDefault();
        // Modern browsers ignore the message but honour preventDefault().
        e.returnValue = '';
        return '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [formData]);

  const acceptResume = useCallback(() => {
    if (!pendingResume) return;
    setFormData(pendingResume.formData);
    setCurrentStep(pendingResume.currentStep || 0);
    // Also restore the wizard mode. Without this, a Resume from the Review
    // step wouldn't know whether the applicant was in full or credit-line
    // mode and the Vehicle-step gating (empty borrow_amount) would misfire.
    if (pendingResume.applicationMode !== undefined) {
      setApplicationMode(pendingResume.applicationMode);
    }
    setPendingResume(null);
  }, [pendingResume]);

  const dismissResume = useCallback(() => {
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
    setPendingResume(null);
  }, []);

  const updateFormData = useCallback((updates) => {
    setFormData(prev => ({
      ...prev,
      ...updates
    }));
  }, []);

  const updateNestedFormData = useCallback((path, value) => {
    setFormData(prev => {
      const keys = path.split('.');
      const newData = { ...prev };
      let current = newData;
      
      for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        if (key.includes('[')) {
          const [arrayKey, indexStr] = key.split('[');
          const index = parseInt(indexStr.replace(']', ''));
          current[arrayKey] = [...current[arrayKey]];
          current[arrayKey][index] = { ...current[arrayKey][index] };
          current = current[arrayKey][index];
        } else {
          current[key] = { ...current[key] };
          current = current[key];
        }
      }
      
      current[keys[keys.length - 1]] = value;
      return newData;
    });
  }, []);

  const applyExtractedData = useCallback(async (data) => {
    if (!data) return;

    // Mark this session as ephemeral — the form was filled from OCR, almost
    // certainly by staff for someone else. We don't want the applicant's PII
    // sitting in this device's localStorage. Also clear any prior saved
    // progress so the Resume modal won't reappear next session and leak data
    // between customers.
    setIsEphemeral(true);
    setPendingResume(null);
    // OCR-extracted submissions go straight into the full vehicle flow — the
    // dealer has already pasted vehicle data so the "What are you applying for?"
    // choice would just be friction.
    setApplicationMode('full');
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }

    console.log('Extracted data received:', JSON.stringify(data, null, 2));
    
    const updates = {};
    
    // Map extracted data to form fields
    if (data.title) updates.title = data.title;
    if (data.first_name) updates.first_name = titleCaseName(data.first_name);
    if (data.surname) updates.surname = titleCaseName(data.surname);
    if (data.date_of_birth) updates.date_of_birth = data.date_of_birth;
    if (data.nationality) updates.nationality = data.nationality;
    if (data.marital_status) updates.marital_status = data.marital_status;
    if (data.driving_licence_type) updates.driving_licence_type = data.driving_licence_type;
    if (data.has_uk_passport != null) updates.has_uk_passport = data.has_uk_passport;
    if (data.dependants != null) updates.dependants = data.dependants;
    if (data.monthly_mortgage_rent != null && parseFloat(data.monthly_mortgage_rent) >= 0) {
      updates.monthly_mortgage_rent = String(data.monthly_mortgage_rent);
    }
    const extraIncomes = (Array.isArray(data.additional_incomes) ? data.additional_incomes : [])
      .filter((r) => r && (r.description || '').trim() && parseFloat(r.monthly_amount) > 0)
      .slice(0, 3)
      .map((r) => ({ description: r.description.trim(), monthly_amount: String(Math.round(parseFloat(r.monthly_amount))) }));
    if (extraIncomes.length) {
      updates.has_additional_income = 'yes';
      updates.additional_incomes = extraIncomes;
    }
    // Auto-derive gender from title if not explicitly provided
    if (data.gender) {
      updates.gender = data.gender;
    } else if (data.title) {
      const titleGenderMap = { Mr: 'Male', Mrs: 'Female', Miss: 'Female', Ms: 'Female' };
      updates.gender = titleGenderMap[data.title] || '';
    }
    if (data.employment_status) updates.employment_status = data.employment_status;
    if (data.employer) updates.employer = titleCaseCompany(data.employer);
    if (data.job_title) updates.job_title = data.job_title;
    if (data.employment_years != null) updates.employment_years = data.employment_years;
    if (data.employment_months != null) updates.employment_months = data.employment_months;
    if (data.employer_phone) updates.employer_phone = data.employer_phone;
    if (data.employer_address) {
      updates.employer_address = {
        postcode: data.employer_address.postcode || '',
        building_number: data.employer_address.building_number || '',
        street: data.employer_address.street || '',
        town: data.employer_address.town || ''
      };
    }
    // Prefer explicit OCR-extracted monthly_income; only fall back to UK PAYE calc when missing
    const ocrMonthlyIncome = parseFloat(data.monthly_income);
    const hasExplicitMonthlyIncome = Number.isFinite(ocrMonthlyIncome) && ocrMonthlyIncome > 0;

    if (data.gross_annual_income !== null && data.gross_annual_income !== undefined) {
      const grossAnnual = parseFloat(data.gross_annual_income) || 0;
      updates.gross_annual_income = data.gross_annual_income.toString();

      if (grossAnnual > 0) {
        const grossMonthly = Math.round(grossAnnual / 12);
        updates.gross_monthly_income = grossMonthly.toString();

        if (hasExplicitMonthlyIncome) {
          // Trust explicit OCR value (e.g. proposal states "Net Monthly Income £2,450")
          updates.monthly_income = Math.round(ocrMonthlyIncome).toString();
        } else {
          // Fall back to UK PAYE Tax + NI calculation (matches AutoConvert)
          const pa = 12570;
          const basicThreshold = 50270;
          let tax = 0, ni = 0;
          if (grossAnnual > pa) {
            const basic = Math.min(grossAnnual, basicThreshold) - pa;
            tax += basic * 0.20;
            ni += basic * 0.08;
            if (grossAnnual > basicThreshold) {
              const higher = grossAnnual - basicThreshold;
              tax += higher * 0.40;
              ni += higher * 0.02;
            }
          }
          const netMonthly = Math.round((grossAnnual - tax - ni) / 12);
          updates.monthly_income = netMonthly.toString();
        }
      }
    } else if (hasExplicitMonthlyIncome) {
      // No gross annual, but OCR gave us monthly net — still capture it
      updates.monthly_income = Math.round(ocrMonthlyIncome).toString();
    }
    
    // Handle employment history (multiple employments)
    if (data.employment_history && data.employment_history.length > 0) {
      updates.employment_history = data.employment_history.map((emp, index) => {
        // Use explicit number conversion and handle 0 values correctly
        let years = 0;
        let months = 0;
        
        // First try emp.years/months
        if (typeof emp.years === 'number') {
          years = emp.years;
        } else if (index === 0 && typeof data.employment_years === 'number') {
          years = data.employment_years;
        }
        
        if (typeof emp.months === 'number') {
          months = emp.months;
        } else if (index === 0 && typeof data.employment_months === 'number') {
          months = data.employment_months;
        }
        
        console.log(`Creating employment_history[${index}] with years=${years}, months=${months}`);
        
        // Get employer address - check if it has actual values
        let empAddress = emp.employer_address;
        if (!empAddress || (!empAddress.postcode && !empAddress.building_number && !empAddress.street && !empAddress.town)) {
          // If emp.employer_address is empty, try to use top-level employer_address for first entry
          if (index === 0 && data.employer_address) {
            empAddress = data.employer_address;
          } else {
            empAddress = { postcode: '', building_number: '', street: '', town: '' };
          }
        }
        
        return {
          employer: titleCaseCompany(emp.employer || (index === 0 ? data.employer : '') || ''),
          job_title: emp.job_title || (index === 0 ? data.job_title : '') || '',
          employment_status: emp.employment_status || (index === 0 ? data.employment_status : '') || '',
          years: years,
          months: months,
          employer_phone: emp.employer_phone || (index === 0 ? data.employer_phone : '') || '',
          employer_address_same_as_home: false,
          employer_address: {
            postcode: empAddress.postcode || '',
            building_number: empAddress.building_number || empAddress.building || '',
            street: empAddress.street || '',
            town: empAddress.town || ''
          }
        };
      });
      
      // Also set main employment_years and employment_months from first entry
      if (updates.employment_history[0]) {
        updates.employment_years = updates.employment_history[0].years;
        updates.employment_months = updates.employment_history[0].months;
        console.log('Setting main employment_years to:', updates.employment_years);
      }
      
      // Also set main fields from first employment
      if (data.employment_history[0]) {
        const firstEmp = data.employment_history[0];
        console.log('First employment data:', JSON.stringify(firstEmp, null, 2));
        
        if (firstEmp.employer) updates.employer = titleCaseCompany(firstEmp.employer);
        if (firstEmp.job_title) updates.job_title = firstEmp.job_title;
        if (firstEmp.employment_status) updates.employment_status = firstEmp.employment_status;
        if (firstEmp.employer_phone) updates.employer_phone = firstEmp.employer_phone;
        if (firstEmp.employer_address && (firstEmp.employer_address.postcode || firstEmp.employer_address.building_number || firstEmp.employer_address.building || firstEmp.employer_address.street || firstEmp.employer_address.town)) {
          updates.employer_address = {
            postcode: firstEmp.employer_address.postcode || '',
            building_number: firstEmp.employer_address.building_number || firstEmp.employer_address.building || '',
            street: firstEmp.employer_address.street || '',
            town: firstEmp.employer_address.town || ''
          };
        } else if (data.employer_address) {
          // Fallback to top-level employer_address
          updates.employer_address = {
            postcode: data.employer_address.postcode || '',
            building_number: data.employer_address.building_number || data.employer_address.building || '',
            street: data.employer_address.street || '',
            town: data.employer_address.town || ''
          };
        }
      }
    } else {
      // No employment_history array - use top-level values if available
      if (typeof data.employment_years === 'number') {
        updates.employment_years = data.employment_years;
        // Also create an employment_history entry
        updates.employment_history = [{
          employer: data.employer || '',
          job_title: data.job_title || '',
          employment_status: data.employment_status || '',
          years: data.employment_years,
          months: typeof data.employment_months === 'number' ? data.employment_months : 0,
          employer_phone: data.employer_phone || '',
          employer_address_same_as_home: false,
          employer_address: data.employer_address || { postcode: '', building_number: '', street: '', town: '' }
        }];
      }
      if (typeof data.employment_months === 'number') {
        updates.employment_months = data.employment_months;
      }
    }
    if (data.sort_code) updates.sort_code = data.sort_code;
    if (data.bank_name) updates.bank_name = data.bank_name;
    if (data.account_number) updates.account_number = data.account_number;
    if (data.bank_years != null) updates.bank_years = data.bank_years;
    if (data.bank_months != null) updates.bank_months = data.bank_months;
    // Fallback: if extraction caught the sort code but not the bank name, look it up
    // from the sort code via our backend mapping. Saves the user from having to visit
    // the Bank step just to trigger the auto-fill.
    if (data.sort_code && !data.bank_name) {
      const sortDigits = String(data.sort_code).replace(/\D/g, '');
      if (sortDigits.length === 6) {
        try {
          const resp = await fetch(`${API_URL}/api/bank-lookup/${sortDigits}`);
          const body = await resp.json();
          if (body?.success && body?.bank_name) {
            updates.bank_name = body.bank_name;
          }
        } catch { /* network blip — bank step will retry on visit */ }
      }
    }
    if (data.vehicle_registration) {
      updates.vehicle_registration = data.vehicle_registration;
      updates.vehicle_reg_unknown = false;
    }
    if (data.vehicle_mileage) {
      // Clean mileage - remove "miles", commas, spaces, and any non-numeric characters
      const cleanedMileage = data.vehicle_mileage.toString().replace(/[^\d]/g, '');
      updates.vehicle_mileage = cleanedMileage;
    }
    if (data.vehicle_type) updates.vehicle_type = data.vehicle_type;
    // DVLA vehicle detail fields from OCR
    if (data.vehicle_make) updates.vehicle_make = data.vehicle_make;
    if (data.vehicle_model) updates.vehicle_model = data.vehicle_model;
    if (data.vehicle_colour) updates.vehicle_colour = data.vehicle_colour;
    if (data.vehicle_fuel_type) updates.vehicle_fuel_type = data.vehicle_fuel_type;
    if (data.vehicle_derivative) updates.vehicle_derivative = data.vehicle_derivative;

    // Auto-trigger DVLA lookup if registration was extracted
    if (data.vehicle_registration && data.vehicle_registration.trim()) {
      try {
        const reg = data.vehicle_registration.trim().replace(/\s/g, '').toUpperCase();
        const dvlaRes = await fetch(`${API_URL}/api/vehicle-lookup/${encodeURIComponent(reg)}`);
        const dvlaData = await dvlaRes.json();
        if (dvlaData.success) {
          // Only override with DVLA data if it's non-empty — preserve OCR-extracted values
          if (dvlaData.make) updates.vehicle_make = dvlaData.make;
          if (dvlaData.model) updates.vehicle_model = dvlaData.model;
          // Always take DVLA colour/year/fuel/engine (more reliable than OCR for these)
          updates.vehicle_colour = dvlaData.colour || '';
          updates.vehicle_year = dvlaData.year_of_manufacture?.toString() || '';
          updates.vehicle_fuel_type = dvlaData.fuel_type || '';
          updates.vehicle_engine_capacity = dvlaData.engine_capacity?.toString() || '';
          updates.vehicle_date_first_registered = dvlaData.date_first_registered || '';
          updates.vehicle_co2_emissions = dvlaData.co2_emissions?.toString() || '';
          updates.vehicle_tax_status = dvlaData.tax_status || '';
          updates.vehicle_mot_status = dvlaData.mot_status || '';
          updates.vehicle_mot_expiry = dvlaData.mot_expiry_date || '';
          if (dvlaData.vehicle_type) updates.vehicle_type = dvlaData.vehicle_type;
          console.log('DVLA auto-lookup completed:', dvlaData.make, dvlaData.vehicle_type);
        }
      } catch (err) {
        console.warn('DVLA auto-lookup failed:', err);
      }
      try {
        const reg = data.vehicle_registration.trim().replace(/\s/g, '').toUpperCase();
        const mot = await (await fetch(`${API_URL}/api/mot-history/${encodeURIComponent(reg)}`)).json();
        if (mot.available && mot.model && !updates.vehicle_model) updates.vehicle_model = mot.model;
        if (mot.available && mot.has_history) {
          updates.mot_last_mileage = mot.last_mileage ? String(mot.last_mileage) : '';
          updates.mot_last_date = mot.last_date || '';
        }
      } catch (err) {
        console.warn('MOT history lookup failed:', err);
      }
    }
    if (data.cash_price != null) {
      updates.cash_price = data.cash_price.toString();
      updates.borrow_amount = data.cash_price.toString();
    }
    if (data.deposit != null) updates.deposit = data.deposit.toString();
    if (data.part_exchange != null) {
      updates.part_exchange = data.part_exchange.toString();
    }
    if (data.settlement != null) {
      updates.settlement = data.settlement.toString();
    }
    // Map VAT qualifying from OCR/auto-detection
    if (data.vat_qualifying === true) updates.vat_qualifying = true;
    if (data.mobile_number) {
      // Normalize UK phone: +44/0044/44 prefix to 0 prefix
      let phone = data.mobile_number.replace(/[\s\-()]/g, '');
      if (phone.startsWith('+44')) phone = '0' + phone.slice(3);
      else if (phone.startsWith('0044')) phone = '0' + phone.slice(4);
      else if (phone.startsWith('44') && phone.length >= 12) phone = '0' + phone.slice(2);
      updates.mobile_number = phone;
    }
    if (data.email_address) updates.email_address = data.email_address;
    
    // Fallback: if no gross_annual_income at top level, try first employment_history entry
    if (!updates.gross_annual_income && data.employment_history?.length > 0) {
      const firstEmpIncome = data.employment_history[0]?.gross_annual_income;
      if (firstEmpIncome != null && firstEmpIncome > 0) {
        const grossAnnual = parseFloat(firstEmpIncome) || 0;
        updates.gross_annual_income = grossAnnual.toString();
        const grossMonthly = Math.round(grossAnnual / 12);
        const pa = 12570;
        const basicThreshold = 50270;
        let tax = 0, ni = 0;
        if (grossAnnual > pa) {
          const basic = Math.min(grossAnnual, basicThreshold) - pa;
          tax += basic * 0.20;
          ni += basic * 0.08;
          if (grossAnnual > basicThreshold) {
            const higher = grossAnnual - basicThreshold;
            tax += higher * 0.40;
            ni += higher * 0.02;
          }
        }
        const netMonthly = Math.round((grossAnnual - tax - ni) / 12);
        updates.gross_monthly_income = grossMonthly.toString();
        updates.monthly_income = hasExplicitMonthlyIncome
          ? Math.round(ocrMonthlyIncome).toString()
          : netMonthly.toString();
      }
    }
    
    // Fallback: if still no gross_annual_income, derive from gross_monthly_income
    if (!updates.gross_annual_income && data.gross_monthly_income) {
      const gm = parseFloat(data.gross_monthly_income) || 0;
      if (gm > 0) {
        const grossAnnual = Math.round(gm * 12);
        updates.gross_annual_income = grossAnnual.toString();
        const grossMonthly = Math.round(grossAnnual / 12);
        const pa = 12570;
        const basicThreshold = 50270;
        let tax = 0, ni = 0;
        if (grossAnnual > pa) {
          const basic = Math.min(grossAnnual, basicThreshold) - pa;
          tax += basic * 0.20;
          ni += basic * 0.08;
          if (grossAnnual > basicThreshold) {
            const higher = grossAnnual - basicThreshold;
            tax += higher * 0.40;
            ni += higher * 0.02;
          }
        }
        const netMonthly = Math.round((grossAnnual - tax - ni) / 12);
        updates.gross_monthly_income = grossMonthly.toString();
        updates.monthly_income = hasExplicitMonthlyIncome
          ? Math.round(ocrMonthlyIncome).toString()
          : netMonthly.toString();
        console.log('Derived gross_annual_income from gross_monthly_income:', grossAnnual);
      }
    }
    
    // Handle term
    if (data.term !== null && data.term !== undefined) {
      updates.term = data.term;
    }
    if (data.finance_type) updates.finance_type = data.finance_type;
    if (data.annual_mileage != null) updates.annual_mileage = data.annual_mileage;
    
    // Handle application type and business details
    if (data.application_type === 'Business') {
      updates.application_type = 'Business';
      if (data.business_details) {
        // Map business type from extracted text to enum values
        const businessTypeMap = {
          'Private Limited Company': 'LimitedCompany',
          'Limited Company': 'LimitedCompany',
          'Public Limited Company': 'PLC',
          'PLC': 'PLC',
          'Partnership': 'Partnership',
          'Limited Liability Partnership': 'LLP',
          'LLP': 'LLP',
          'Sole Trader': 'SoleTrader',
          'Other': 'Other'
        };
        const mappedBusinessType = businessTypeMap[data.business_details.business_type] || data.business_details.business_type || '';
        
        updates.business_details = {
          business_type: mappedBusinessType,
          industry: data.business_details.industry || '',
          business_name: titleCaseCompany(data.business_details.business_name || ''),
          main_telephone: data.business_details.main_telephone || '',
          date_of_incorporation: data.business_details.date_of_incorporation || '',
          previous_business_name: titleCaseCompany(data.business_details.previous_business_name || ''),
          registration_number: data.business_details.registration_number || data.business_details.company_registration_number || '',
          vat_number: data.business_details.vat_number || '',
          // Default to UK/England if not specified
          country_of_operation: data.business_details.country_of_operation || 'United Kingdom',
          country_of_incorporation: data.business_details.country_of_incorporation || 'England',
          business_description: data.business_details.business_description || '',
          business_address: data.business_details.business_address ? {
            premises_type: data.business_details.business_address.premises_type || 'Leased',
            postcode: data.business_details.business_address.postcode || '',
            building: data.business_details.business_address.building || data.business_details.business_address.building_number || '',
            street: data.business_details.business_address.street || '',
            town: data.business_details.business_address.town || '',
            county: data.business_details.business_address.county || '',
            years: data.business_details.business_address.years || 0,
            months: data.business_details.business_address.months || 0
          } : {}
        };
      }
    } else if (data.application_type === 'Private') {
      updates.application_type = 'Private';
      // Reset business details when switching to Private
      updates.business_details = initialFormData.business_details;
    }
    
    // Handle addresses. Include sub_building + county which are optional but
    // populated by the postcode-lookup and by Scottish flat detection — must
    // survive the OCR merge otherwise the Review + Artis payload lose them.
    // Street/town/county are title-cased so OCR "DOWNING STREET" → "Downing Street".
    if (data.addresses && data.addresses.length > 0) {
      updates.addresses = data.addresses.map(addr => ({
        postcode: addr.postcode || '',
        sub_building: addr.sub_building || '',
        house_number: addr.house_number || '',
        house_name: titleCaseCompany(addr.house_name || ''),
        street: titleCaseCompany(addr.street || ''),
        town: titleCaseCompany(addr.town || ''),
        county: titleCaseCompany(addr.county || ''),
        years: addr.years || 0,
        months: addr.months || 0,
        residential_status: addr.residential_status || ''
      }));
    }

    // Dealer name auto-cap from OCR (rare but harmless)
    if (data.dealer_name) updates.dealer_name = titleCaseCompany(data.dealer_name);
    
    console.log('Updates to apply:', JSON.stringify(updates, null, 2));
    console.log('employment_history in updates:', JSON.stringify(updates.employment_history, null, 2));
    console.log('employment_years in updates:', updates.employment_years);
    console.log('employment_months in updates:', updates.employment_months);
    
    setExtractedData(data);
    updateFormData(updates);
  }, [updateFormData]);

  const resetForm = useCallback(() => {
    setFormData(initialFormData);
    setCurrentStep(0);
    setExtractedData(null);
    setIsEphemeral(false);
    setApplicationMode(null);
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
  }, []);

  const nextStep = useCallback(() => {
    setCurrentStep(prev => prev + 1);
  }, []);

  const prevStep = useCallback(() => {
    setCurrentStep(prev => Math.max(0, prev - 1));
  }, []);

  const goToStep = useCallback((step) => {
    setCurrentStep(step);
  }, []);

  return (
    <FormContext.Provider value={{
      formData,
      updateFormData,
      updateNestedFormData,
      currentStep,
      setCurrentStep,
      nextStep,
      prevStep,
      goToStep,
      extractedData,
      setExtractedData,
      applyExtractedData,
      isProcessing,
      setIsProcessing,
      resetForm,
      editingFromReview,
      setEditingFromReview,
      pendingResume,
      acceptResume,
      dismissResume,
      applicationMode,
      setApplicationMode,
    }}>
      {children}
    </FormContext.Provider>
  );
};

export const useForm = () => {
  const context = useContext(FormContext);
  if (!context) {
    throw new Error('useForm must be used within a FormProvider');
  }
  return context;
};

export default FormContext;
